import axios from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
} from '@/services/auth.js';

const baseURL = import.meta.env.VITE_API_BASE_URL;

const apiClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' }
});

apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let pendingQueue = [];

const resolveQueue = (token) => {
  pendingQueue.forEach((cb) => cb(token));
  pendingQueue = [];
};

function ehRequisicaoPublica(url) {
  if (!url) return false;
  const caminho = url.toLowerCase();
  return caminho.includes('editais/publicados')
    || (caminho.includes('editais/') && caminho.includes('/arquivo'))
    || (caminho.includes('editais/') && caminho.includes('/anexos/'))
    || caminho.includes('usuario/login')
    || caminho.includes('usuario/criarconta')
    || caminho.includes('usuario/verificarusuario');
}

const redirecionarParaLogin = () => {
  clearTokens();
  localStorage.removeItem('usuarioLogado');
  localStorage.removeItem('isAdmin');
  localStorage.removeItem('perfis');

  const areaDoCandidato = `${import.meta.env.BASE_URL}candidato`;
  const ehAreaDoCandidato = window.location.pathname.toLowerCase().startsWith(areaDoCandidato.toLowerCase());
  const loginPath = ehAreaDoCandidato ? `${areaDoCandidato}/login` : `${import.meta.env.BASE_URL}login`;

  if (window.location.pathname.toLowerCase() !== loginPath.toLowerCase()) {
    window.location.href = loginPath;
  }
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if (!original || status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    if (original.url && original.url.toLowerCase().includes('usuario/login')) {
      return Promise.reject(error);
    }

    if (original.url && original.url.includes('Usuario/refresh')) {
      redirecionarParaLogin();
      return Promise.reject(error);
    }

    if (ehRequisicaoPublica(original.url)) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push((token) => {
          if (!token) return reject(error);
          original.headers.Authorization = `Bearer ${token}`;
          resolve(apiClient(original));
        });
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) throw error;

      const { data } = await axios.post(
        `${baseURL}Usuario/refresh`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      );

      setTokens(data.accessToken, data.refreshToken, data.refreshTokenExpiresAt);
      resolveQueue(data.accessToken);

      original.headers.Authorization = `Bearer ${data.accessToken}`;
      return apiClient(original);
    } catch (refreshErr) {
      resolveQueue(null);
      if (!ehRequisicaoPublica(original.url)) {
        redirecionarParaLogin();
      }
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
